import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { api } from '../utils/api';
import {
  Table,
  Card,
  Typography,
  Tag,
  Input,
  Select,
  Space,
  Statistic,
  Row,
  Col,
  Spin,
  Button,
  Modal,
  Form,
  Popconfirm,
  Avatar,
  Switch,
  Tabs,
  Divider,
  Drawer,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  List,
  Empty,
  Flex
} from 'antd';
import { message } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  PieChartOutlined,
  TrendingUpOutlined,
  WalletOutlined,
  MoreOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  IdcardOutlined,
  TeamOutlined,
  FilterOutlined,
  MenuOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatToIST, formatDateIST, formatTimeIST, formatShortTimeIST, isTodayIST, nowIST } from '../utils/dateUtils';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Mobile-friendly User Card Component - Memoized for performance
const UserCard = memo(({ user, isMobile, onAction }) => {
  // Memoize expensive calculations
  const portfolioValue = useMemo(() => {
    return user.portfolio?.reduce((sum, holding) =>
      sum + (holding.quantity * (holding.currentPrice || holding.avgPrice) * (holding.lotSize || 1)), 0
    ) || 0;
  }, [user.portfolio]);
  
  const totalCapital = useMemo(() => {
    return (user.balance || 0) + portfolioValue;
  }, [user.balance, portfolioValue]);

  const getKycStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#52c41a';
      case 'in_progress': return '#1890ff';
      case 'rejected': return '#ff4d4f';
      default: return '#faad14';
    }
  };

  const actionItems = [
    {
      key: 'view',
      label: 'View Details',
      icon: <EyeOutlined />,
      onClick: () => onAction('view', user)
    },
    {
      key: 'edit',
      label: 'Edit User',
      icon: <EditOutlined />,
      onClick: () => onAction('edit', user)
    },
    {
      key: 'financial',
      label: 'Manage Funds',
      icon: <WalletOutlined />,
      onClick: () => onAction('financial', user)
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: <PieChartOutlined />,
      onClick: () => onAction('portfolio', user)
    },
    {
      key: 'transactions',
      label: 'Transactions',
      icon: <BankOutlined />,
      onClick: () => onAction('transactions', user)
    },
    {
      key: 'delete',
      label: 'Delete User',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: 'Delete User',
          content: `Are you sure you want to delete ${user.name}?`,
          okText: 'Yes, Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => onAction('delete', user)
        });
      }
    }
  ];

  const actionMenu = (
    <Menu
      items={actionItems.map(item => ({
        key: item.key,
        label: item.label,
        icon: item.icon,
        danger: item.danger,
        onClick: item.onClick
      }))}
    />
  );

  return (
    <Card
      hoverable
      style={{
        borderRadius: '16px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.3s ease',
        height: '100%'
      }}
      bodyStyle={{ padding: '20px' }}
    >
      {/* User Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar
          size={48}
          style={{
            backgroundColor: user.role === 'admin' ? '#722ed1' : '#1890ff',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          {user.name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        
        <div style={{ marginLeft: '12px', flex: 1 }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#1e293b',
            marginBottom: '2px'
          }}>
            {user.name || 'N/A'}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {user.email}
          </div>
        </div>
        
        <Dropdown overlay={actionMenu} trigger={['click']} placement="bottomRight">
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            style={{ 
              color: '#64748b',
              border: 'none',
              boxShadow: 'none'
            }}
          />
        </Dropdown>
      </div>

      {/* User Info */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <PhoneOutlined style={{ color: '#64748b', marginRight: '8px' }} />
          <span style={{ fontSize: '13px', color: '#374151' }}>
            {user.phone || 'N/A'}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <CalendarOutlined style={{ color: '#64748b', marginRight: '8px' }} />
          <span style={{ fontSize: '13px', color: '#374151' }}>
            Joined {formatDateIST(user.createdAt)}
          </span>
        </div>
      </div>

      {/* Status Tags */}
      <div style={{ marginBottom: '16px' }}>
        <Space wrap>
          <Tag 
            color={user.role === 'admin' ? 'purple' : 'blue'}
            style={{ fontWeight: '500', borderRadius: '6px' }}
          >
            {user.role?.toUpperCase()}
          </Tag>
          
          <Tag 
            color={getKycStatusColor(user.trading?.kycStatus)}
            style={{ fontWeight: '500', borderRadius: '6px' }}
          >
            KYC: {user.trading?.kycStatus?.toUpperCase() || 'PENDING'}
          </Tag>
          
          {user.isVerified ? (
            <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: '6px' }}>
              Verified
            </Tag>
          ) : (
            <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ borderRadius: '6px' }}>
              Unverified
            </Tag>
          )}
        </Space>
      </div>

      {/* Financial Info */}
      <div style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Balance:</span>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
            ₹{user.balance?.toLocaleString() || '0'}
          </span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Portfolio:</span>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed' }}>
            ₹{portfolioValue.toLocaleString()}
          </span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Total Capital:</span>
          <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
            ₹{totalCapital.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button 
          type="primary" 
          size="small" 
          icon={<EyeOutlined />}
          onClick={() => onAction('view', user)}
          style={{ flex: 1, borderRadius: '8px' }}
        >
          View
        </Button>
        
        <Button 
          type="primary" 
          size="small" 
          icon={<WalletOutlined />}
          onClick={() => onAction('financial', user)}
          style={{ 
            flex: 1, 
            borderRadius: '8px',
            background: '#52c41a',
            borderColor: '#52c41a'
          }}
        >
          Funds
        </Button>
      </div>
    </Card>
  );
});

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogMode, setDialogMode] = useState('view'); // 'view', 'edit', 'create'
  const [tabValue, setTabValue] = useState('1');
  const [form] = Form.useForm();
  
  // Mobile responsive states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'table' or 'cards'
  const [searchDebounced, setSearchDebounced] = useState('');

  // Financial Management States
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [selectedUserFinancial, setSelectedUserFinancial] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [fundsForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
    
    // Optimized resize handler with throttling
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);
        setViewMode(mobile ? 'cards' : 'table');
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);
  
  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchText);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    filterUsers();
  }, [users, searchDebounced, roleFilter, kycFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.getUsers();
      setUsers(response.data.users || response.data);
    } catch (error) {
      message.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchDebounced) {
      const searchLower = searchDebounced.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.phone?.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // KYC filter
    if (kycFilter !== 'all') {
      filtered = filtered.filter(user => user.trading?.kycStatus === kycFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchDebounced, roleFilter, kycFilter]);

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    setSelectedUser(user);

    if (mode === 'create') {
      form.resetFields();
    } else if (user) {
      form.setFieldsValue({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'user',
        balance: user.balance || 0,
        isVerified: user.isVerified || false,
        address: typeof user.profile?.address === 'string' ? user.profile.address : user.profile?.address?.street || '',
        dateOfBirth: user.profile?.dateOfBirth ? new Date(user.profile.dateOfBirth).toISOString().split('T')[0] : '',
        gender: user.profile?.gender || '',
        occupation: user.profile?.occupation || '',
        panNumber: user.documents?.pan?.number || '',
        panVerified: user.documents?.pan?.verified || false,
        aadharNumber: user.documents?.aadhar?.number || '',
        aadharVerified: user.documents?.aadhar?.verified || false,
        accountNumber: user.documents?.bankAccount?.accountNumber || '',
        ifscCode: user.documents?.bankAccount?.ifscCode || '',
        bankName: user.documents?.bankAccount?.bankName || '',
        accountHolderName: user.documents?.bankAccount?.accountHolderName || '',
        bankVerified: user.documents?.bankAccount?.verified || false,
        experience: user.trading?.experience || 'beginner',
        riskTolerance: user.trading?.riskTolerance || 'medium',
        kycStatus: user.trading?.kycStatus || 'pending',
        emailNotifications: user.preferences?.notifications?.email ?? true,
        smsNotifications: user.preferences?.notifications?.sms ?? true,
        pushNotifications: user.preferences?.notifications?.push ?? true,
        theme: user.preferences?.theme || 'light',
        language: user.preferences?.language || 'en'
      });
    }

    setTabValue('1');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setTabValue('1');
  };

  const handleSaveUser = async (values) => {
    try {
      const userData = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        role: values.role || 'user',
        balance: parseFloat(values.balance) || 0,
        isVerified: values.isVerified || false
      };

      // Only add optional fields if they have values
      if (values.address || values.dateOfBirth || values.gender || values.occupation) {
        userData.profile = {
          address: {
            street: values.address || '',
            city: '',
            state: '',
            pincode: '',
            country: 'India'
          },
          dateOfBirth: values.dateOfBirth || null,
          gender: values.gender || null,
          occupation: values.occupation || null
        };
      }

      // Only add documents if they have values
      if (values.panNumber || values.aadharNumber || values.accountNumber) {
        userData.documents = {};
        
        if (values.panNumber) {
          userData.documents.pan = {
            number: values.panNumber,
            verified: values.panVerified || false
          };
        }
        
        if (values.aadharNumber) {
          userData.documents.aadhar = {
            number: values.aadharNumber,
            verified: values.aadharVerified || false
          };
        }
        
        if (values.accountNumber) {
          userData.documents.bankAccount = {
            accountNumber: values.accountNumber,
            ifscCode: values.ifscCode || '',
            bankName: values.bankName || '',
            accountHolderName: values.accountHolderName || '',
            verified: values.bankVerified || false
          };
        }
      }

      // Only add trading info if values exist
      if (values.experience || values.riskTolerance || values.kycStatus) {
        userData.trading = {
          experience: values.experience || 'beginner',
          riskTolerance: values.riskTolerance || 'medium',
          kycStatus: values.kycStatus || 'pending'
        };
      }

      // Only add preferences if values exist
      if (values.emailNotifications !== undefined || values.smsNotifications !== undefined || 
          values.pushNotifications !== undefined || values.theme || values.language) {
        userData.preferences = {
          notifications: {
            email: values.emailNotifications !== undefined ? values.emailNotifications : true,
            sms: values.smsNotifications !== undefined ? values.smsNotifications : true,
            push: values.pushNotifications !== undefined ? values.pushNotifications : true
          },
          theme: values.theme || 'light',
          language: values.language || 'en'
        };
      }

      // Add password only for new user creation
      if (dialogMode === 'create') {
        if (!values.password) {
          message.error('Password is required for new user creation');
          return;
        }
        if (values.password.length < 6) {
          message.error('Password must be at least 6 characters long');
          return;
        }
        userData.password = values.password;
      }

      // Validate PAN number format if provided
      if (values.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(values.panNumber)) {
        message.error('Invalid PAN number format. Please enter a valid PAN number.');
        return;
      }

      // Validate Aadhar number format if provided
      if (values.aadharNumber && !/^[0-9]{12}$/.test(values.aadharNumber)) {
        message.error('Aadhar number must be exactly 12 digits.');
        return;
      }

      if (dialogMode === 'create') {
        await api.createUser(userData);
      } else {
        await api.updateUser(selectedUser._id, userData);
      }

      await fetchUsers();
      handleCloseDialog();
      message.success(
        `User ${dialogMode === 'create' ? 'created' : 'updated'} successfully`
      );
    } catch (error) {
      console.error('User creation/update error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to save user';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.deleteUser(userId);
      await fetchUsers();
      message.success('User deleted successfully');
    } catch (error) {
      message.error('Failed to delete user');
    }
  };

  const handleKycStatusChange = async (userId, status) => {
    try {
      await api.put('/api/profile/kyc-status', { userId, status });
      await fetchUsers();
      message.success('KYC status updated successfully');
    } catch (error) {
      message.error('Failed to update KYC status');
    }
  };

  const handleDocumentVerification = async (userId, documentType, verified) => {
    try {
      await api.put('/api/profile/kyc-status', { userId, documentType, verified });
      await fetchUsers();
      message.success('Document verification updated successfully');
    } catch (error) {
      message.error('Failed to update document verification');
    }
  };

  // Financial Management Functions
  const handleOpenFinancialModal = async (user) => {
    setSelectedUserFinancial(user);
    setFinancialModalOpen(true);
    setFinancialLoading(true);

    try {
      const response = await api.get(`/api/admin/users/${user._id}/financial-summary`);
      setFinancialData(response.data);
    } catch (error) {
      message.error('Failed to fetch financial data');
    } finally {
      setFinancialLoading(false);
    }
  };

  const handleOpenPortfolioModal = async (user) => {
    setSelectedUserFinancial(user);
    setPortfolioModalOpen(true);
    setFinancialLoading(true);

    try {
      const response = await api.get(`/api/admin/users/${user._id}/portfolio`);
      setPortfolioData(response.data);
    } catch (error) {
      message.error('Failed to fetch portfolio data');
    } finally {
      setFinancialLoading(false);
    }
  };

  const handleFundsOperation = async (values) => {
    try {
      const response = await api.post(`/api/admin/users/${selectedUserFinancial._id}/funds`, values);

      message.success(response.data.message);
      fundsForm.resetFields();
      await fetchUsers();

      // Refresh financial data if modal is still open
      if (financialModalOpen) {
        handleOpenFinancialModal(selectedUserFinancial);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to manage funds');
    }
  };

  const handleBalanceUpdate = async (userId, newBalance) => {
    try {
      await api.put(`/api/admin/users/${userId}/balance`, { balance: newBalance });
      await fetchUsers();
      message.success('Balance updated successfully');
    } catch (error) {
      message.error('Failed to update balance');
    }
  };

  const handlePortfolioUpdate = async (values) => {
    try {
      const { symbol, action, quantity, avgPrice, currentPrice } = values;
      await api.put(`/api/admin/users/${selectedUserFinancial._id}/portfolio/${symbol}`, {
        action,
        quantity: parseFloat(quantity),
        avgPrice: parseFloat(avgPrice),
        currentPrice: parseFloat(currentPrice)
      });

      message.success('Portfolio updated successfully');
      await fetchUsers();

      // Refresh portfolio data
      if (portfolioModalOpen) {
        handleOpenPortfolioModal(selectedUserFinancial);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update portfolio');
    }
  };

  const handleClearPortfolio = async () => {
    try {
      await api.delete(`/api/admin/users/${selectedUserFinancial._id}/portfolio`);
      message.success('Portfolio cleared successfully');
      await fetchUsers();

      // Refresh portfolio data
      if (portfolioModalOpen) {
        handleOpenPortfolioModal(selectedUserFinancial);
      }
    } catch (error) {
      message.error('Failed to clear portfolio');
    }
  };

  const handleOpenTransactionModal = async (user) => {
    setSelectedUserFinancial(user);
    setTransactionModalOpen(true);
    setFinancialLoading(true);

    try {
      const response = await api.get(`/api/admin/users/${user._id}/transactions`);
      setTransactionData(response.data);
    } catch (error) {
      message.error('Failed to fetch transaction history');
    } finally {
      setFinancialLoading(false);
    }
  };

  const getKycStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'processing';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'purple' : 'blue';
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            size={40}
            style={{
              backgroundColor: record.role === 'admin' ? '#722ed1' : '#1890ff',
              marginRight: 12
            }}
          >
            {record.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
              {record.name || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              {record.email}
            </div>
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: 'Contact',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {phone || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)} style={{ fontWeight: 'bold' }}>
          {role?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Financial Info',
      key: 'financial',
      align: 'right',
      render: (_, record) => {
        const portfolioValue = record.portfolio?.reduce((sum, holding) =>
          sum + (holding.quantity * (holding.currentPrice || holding.avgPrice) * (holding.lotSize || 1)), 0
        ) || 0;
        const totalCapital = (record.balance || 0) + portfolioValue;

        return (
          <div>
            <div style={{ fontWeight: '600', color: '#059669', fontSize: '14px' }}>
              Balance: ₹{record.balance?.toLocaleString() || '0'}
            </div>
            <div style={{ fontSize: '12px', color: '#7c3aed' }}>
              Portfolio: ₹{portfolioValue.toLocaleString()}
            </div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>
              Total: ₹{totalCapital.toLocaleString()} | Holdings: {record.portfolio?.length || 0}
            </div>
          </div>
        );
      },
      width: 160,
    },
    {
      title: 'KYC Status',
      key: 'kycStatus',
      render: (_, record) => (
        <Tag color={getKycStatusColor(record.trading?.kycStatus)}>
          {record.trading?.kycStatus?.toUpperCase() || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Personal Info',
      key: 'personalInfo',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>
            DOB: {record.profile?.dateOfBirth ? 
              new Date(record.profile.dateOfBirth).toLocaleDateString('en-IN') : 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Gender: {record.profile?.gender || 'N/A'}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            PAN: {record.documents?.pan?.number || 'N/A'}
          </div>
        </div>
      ),
      width: 140,
    },
    {
      title: 'Verification',
      key: 'verification',
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            {record.isVerified ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Verified
              </Tag>
            ) : (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                Unverified
              </Tag>
            )}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            PAN: {record.documents?.pan?.verified ? '✓' : '✗'} |
            Aadhar: {record.documents?.aadhar?.verified ? '✓' : '✗'} |
            Bank: {record.documents?.bankAccount?.verified ? '✓' : '✗'}
          </div>
        </div>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <div>
          <div style={{ fontSize: '13px', fontWeight: '500' }}>
            {formatDateIST(date)}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            {formatShortTimeIST(date)}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleOpenDialog('view', record)}
            style={{ borderRadius: '6px' }}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenDialog('edit', record)}
            style={{ borderRadius: '6px', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Edit
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<WalletOutlined />}
            onClick={() => handleOpenFinancialModal(record)}
            style={{ borderRadius: '6px', backgroundColor: '#722ed1', borderColor: '#722ed1' }}
          >
            Funds
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<PieChartOutlined />}
            onClick={() => handleOpenPortfolioModal(record)}
            style={{ borderRadius: '6px', backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
          >
            Portfolio
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<BankOutlined />}
            onClick={() => handleOpenTransactionModal(record)}
            style={{ borderRadius: '6px', backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
          >
            History
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ borderRadius: '6px' }}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 350,
    },
  ];

  // Handle user actions from mobile cards - Memoized for performance
  const handleUserAction = useCallback((action, user) => {
    switch (action) {
      case 'view':
        handleOpenDialog('view', user);
        break;
      case 'edit':
        handleOpenDialog('edit', user);
        break;
      case 'delete':
        handleDeleteUser(user._id);
        break;
      case 'financial':
        handleOpenFinancialModal(user);
        break;
      case 'portfolio':
        handleOpenPortfolioModal(user);
        break;
      case 'transactions':
        handleOpenTransactionModal(user);
        break;
      default:
        break;
    }
  }, []);
  
  // Memoized search handler
  const handleSearchChange = useCallback((e) => setSearchText(e.target.value), []);
  
  // Memoized render item function
  const renderUserCard = useCallback((user) => (
    <List.Item key={user._id}>
      <UserCard user={user} isMobile={isMobile} onAction={handleUserAction} />
    </List.Item>
  ), [isMobile, handleUserAction]);

  // Memoized stats calculation for performance
  const stats = useMemo(() => {
    const totalUsers = filteredUsers.length;
    const verifiedUsers = filteredUsers.filter(user => user.isVerified).length;
    const kycCompleted = filteredUsers.filter(user => user.trading?.kycStatus === 'completed').length;
    const adminUsers = filteredUsers.filter(user => user.role === 'admin').length;
    const totalBalance = filteredUsers.reduce((sum, user) => sum + (user.balance || 0), 0);

    return { totalUsers, verifiedUsers, kycCompleted, adminUsers, totalBalance };
  }, [filteredUsers]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Modern Header */}
      <div className="users-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '24px 20px' : '40px 32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '50%',
          transform: 'translate(50%, -50%)'
        }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <TeamOutlined style={{ fontSize: isMobile ? '24px' : '28px' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: isMobile ? '22px' : '32px', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '-0.5px'
              }}>
                User Management
              </Title>
            </div>
            <p style={{ 
              color: 'rgba(255,255,255,0.85)', 
              margin: 0, 
              fontSize: isMobile ? '14px' : '16px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              fontWeight: 400
            }}>
              Manage users, KYC status, and account details
            </p>
          </div>
          
          <Space size={isMobile ? 8 : 12} style={{ zIndex: 1 }}>
            {isMobile ? (
              <>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => handleOpenDialog('create')}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={fetchUsers}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </>
            ) : (
              <>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Filters
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleOpenDialog('create')}
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    color: '#667eea',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: 600
                  }}
                >
                  Add User
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchUsers}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Refresh
                </Button>
              </>
            )}
          </Space>
        </Flex>
      </div>

      {/* Enhanced Statistics */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card className="slide-up stats-card-modern" style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? '80px' : '100px'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', fontSize: isMobile ? '40px' : '60px', color: 'rgba(255,255,255,0.1)' }}>
              <TeamOutlined />
            </div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '14px', fontWeight: 500 }}>Total Users</span>}
              value={stats.totalUsers}
              valueStyle={{ color: 'white', fontSize: isMobile ? '18px' : '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        
        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card className="slide-up stats-card-modern" style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? '80px' : '100px'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', fontSize: isMobile ? '40px' : '60px', color: 'rgba(255,255,255,0.1)' }}>
              <CheckCircleOutlined />
            </div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '14px', fontWeight: 500 }}>Verified</span>}
              value={stats.verifiedUsers}
              valueStyle={{ color: 'white', fontSize: isMobile ? '18px' : '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        
        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card className="slide-up stats-card-modern" style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? '80px' : '100px'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', fontSize: isMobile ? '40px' : '60px', color: 'rgba(255,255,255,0.1)' }}>
              <ExclamationCircleOutlined />
            </div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '14px', fontWeight: 500 }}>KYC Done</span>}
              value={stats.kycCompleted}
              valueStyle={{ color: 'white', fontSize: isMobile ? '18px' : '28px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        
        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card className="slide-up stats-card-modern" style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            border: 'none',
            borderRadius: isMobile ? '12px' : '16px',
            position: 'relative',
            overflow: 'hidden',
            minHeight: isMobile ? '80px' : '100px'
          }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', fontSize: isMobile ? '40px' : '60px', color: 'rgba(255,255,255,0.1)' }}>
              <WalletOutlined />
            </div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '14px', fontWeight: 500 }}>Balance</span>}
              value={stats.totalBalance}
              prefix="₹"
              precision={0}
              valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '24px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Mobile-Friendly Filters */}
      {isMobile ? (
        <>
          <Card 
            style={{ 
              marginBottom: 20, 
              borderRadius: '20px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
            }} 
            className="slide-up"
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="🔍 Search users by name, email, or phone..."
                allowClear
                onChange={handleSearchChange}
                style={{ 
                  borderRadius: '16px',
                  border: '2px solid #f1f5f9',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                size="large"
              />
            </div>
            <Button
              block
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              style={{ 
                borderRadius: '16px',
                height: '48px',
                fontSize: '16px',
                fontWeight: '600',
                background: showFilters 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                  : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderColor: showFilters ? '#3b82f6' : '#e2e8f0',
                color: showFilters ? 'white' : '#475569',
                border: 'none',
                boxShadow: showFilters 
                  ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                Filters 
                {(roleFilter !== 'all' || kycFilter !== 'all') && (
                  <Badge 
                    count={(roleFilter !== 'all' ? 1 : 0) + (kycFilter !== 'all' ? 1 : 0)} 
                    style={{ 
                      backgroundColor: '#ef4444',
                      fontSize: '10px',
                      minWidth: '18px',
                      height: '18px',
                      lineHeight: '18px'
                    }} 
                  />
                )}
              </span>
            </Button>
          </Card>
          
          <Drawer
            title={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12,
                color: 'white'
              }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  padding: '8px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <FilterOutlined style={{ fontSize: '18px' }} />
                </div>
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Filter Users</span>
              </div>
            }
            placement="bottom"
            onClose={() => setShowFilters(false)}
            open={showFilters}
            height={380}
            styles={{
              header: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderBottom: 'none',
                borderRadius: '20px 20px 0 0',
                padding: '20px 24px'
              },
              body: {
                padding: '24px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
              }
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 12, 
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '15px',
                  letterSpacing: '0.5px'
                }}>👤 User Role</label>
                <Select
                  placeholder="Select user role"
                  style={{ 
                    width: '100%',
                    borderRadius: '16px'
                  }}
                  value={roleFilter}
                  onChange={setRoleFilter}
                  size="large"
                >
                  <Option value="all">🌐 All Roles</Option>
                  <Option value="user">👥 User</Option>
                  <Option value="admin">⚡ Admin</Option>
                </Select>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 12, 
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '15px',
                  letterSpacing: '0.5px'
                }}>📋 KYC Status</label>
                <Select
                  placeholder="Select KYC status"
                  style={{ 
                    width: '100%',
                    borderRadius: '16px'
                  }}
                  value={kycFilter}
                  onChange={setKycFilter}
                  size="large"
                >
                  <Option value="all">📊 All Status</Option>
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="in_progress">🔄 In Progress</Option>
                  <Option value="completed">✅ Completed</Option>
                  <Option value="rejected">❌ Rejected</Option>
                </Select>
              </div>
              
              <Button 
                block 
                type="primary" 
                onClick={() => setShowFilters(false)}
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  marginTop: '20px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                  letterSpacing: '0.5px'
                }}
              >
                ✨ Apply Filters
              </Button>
            </Space>
          </Drawer>
        </>
      ) : (
        <Card
          style={{
            marginBottom: 24,
            borderRadius: '16px',
            border: '1px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          className="slide-up"
          bodyStyle={{ padding: '20px' }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={24} lg={12} xl={10}>
              <Search
                placeholder="Search users by name, email, or phone..."
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ 
                  borderRadius: '12px'
                }}
                size="large"
              />
            </Col>
            
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Select
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ 
                  width: '100%', 
                  borderRadius: '12px'
                }}
                placeholder="Role"
                size="large"
              >
                <Option value="all">All Roles</Option>
                <Option value="user">User</Option>
                <Option value="admin">Admin</Option>
              </Select>
            </Col>
            
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Select
                value={kycFilter}
                onChange={setKycFilter}
                style={{ 
                  width: '100%', 
                  borderRadius: '12px'
                }}
                placeholder="KYC"
                size="large"
              >
                <Option value="all">All KYC</Option>
                <Option value="pending">Pending</Option>
                <Option value="in_progress">Progress</Option>
                <Option value="completed">Done</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={8} md={12} lg={8} xl={8}>
              <Space wrap style={{ width: '100%', justifyContent: window.innerWidth > 1200 ? 'flex-end' : 'flex-start' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleOpenDialog('create')}
                  style={{ 
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Add User
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchUsers}
                  style={{ 
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Responsive User List */}
      {isMobile || viewMode === 'cards' ? (
        <div className="slide-up">
          {filteredUsers.length === 0 ? (
            <Card>
              <Empty description="No users found" />
            </Card>
          ) : (
            <List
              grid={{
                gutter: 16,
                xs: 1,
                sm: 1,
                md: 2,
                lg: 2,
                xl: 3,
                xxl: 4,
              }}
              dataSource={filteredUsers}
              pagination={{
                pageSize: isMobile ? 8 : 16,
                showSizeChanger: !isMobile,
                showQuickJumper: !isMobile,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                style: { textAlign: 'center', marginTop: 16 },
                hideOnSinglePage: true
              }}
              renderItem={renderUserCard}
            />
          )}
        </div>
      ) : (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined style={{ color: '#3b82f6' }} />
              <span>All Users</span>
            </div>
          }
          className="slide-up"
        >
          <Table
            columns={columns}
            dataSource={filteredUsers}
            rowKey="_id"
            pagination={{
              total: filteredUsers.length,
              pageSize: 25,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
              hideOnSinglePage: true
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Card>
      )}

      {/* User Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <TeamOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#3b82f6' }} />
            </div>
            <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600' }}>
              {dialogMode === 'create' ? '👤 Add New User' :
                dialogMode === 'edit' ? '✏️ Edit User' : '👁️ User Details'}
            </span>
          </div>
        }
        open={openDialog}
        onCancel={handleCloseDialog}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveUser}
        >
          <Tabs
            activeKey={tabValue}
            onChange={setTabValue}
            items={[
              {
                key: '1',
                label: 'Basic Info',
                children: (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="name"
                        label={<span style={{ fontWeight: 600, color: '#1e293b' }}>👤 Full Name</span>}
                        rules={[{ required: true, message: 'Please enter name' }]}
                      >
                        <Input 
                          placeholder="Enter full name" 
                          disabled={dialogMode === 'view'}
                          style={{
                            height: isMobile ? '48px' : '40px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '16px' : '14px'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="email"
                        label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📧 Email</span>}
                        rules={[
                          { required: true, message: 'Please enter email' },
                          { type: 'email', message: 'Please enter valid email' }
                        ]}
                      >
                        <Input 
                          placeholder="Enter email" 
                          disabled={dialogMode === 'view'}
                          style={{
                            height: isMobile ? '48px' : '40px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '16px' : '14px'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="phone"
                        label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📱 Phone</span>}
                        rules={[{ required: true, message: 'Please enter phone' }]}
                      >
                        <Input 
                          placeholder="Enter phone number" 
                          disabled={dialogMode === 'view'}
                          style={{
                            height: isMobile ? '48px' : '40px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '16px' : '14px'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    {dialogMode === 'create' && (
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item
                          name="password"
                          label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔒 Password</span>}
                          rules={[
                            { required: true, message: 'Please enter password' },
                            { min: 6, message: 'Password must be at least 6 characters' }
                          ]}
                        >
                          <Input.Password 
                            placeholder="Enter password" 
                            style={{
                              height: isMobile ? '48px' : '40px',
                              borderRadius: '12px',
                              fontSize: isMobile ? '16px' : '14px'
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="role"
                        label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔐 Role</span>}
                        rules={[{ required: true, message: 'Please select role' }]}
                      >
                        <Select 
                          placeholder="Select role" 
                          disabled={dialogMode === 'view'}
                          style={{
                            height: isMobile ? '48px' : '40px'
                          }}
                          size={isMobile ? 'large' : 'middle'}
                        >
                          <Option value="user">👤 User</Option>
                          <Option value="admin">👑 Admin</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="balance"
                        label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💰 Balance</span>}
                        rules={[{ required: true, message: 'Please enter balance' }]}
                      >
                        <Input
                          type="number"
                          placeholder="Enter balance"
                          disabled={dialogMode === 'view'}
                          prefix="₹"
                          style={{
                            height: isMobile ? '48px' : '40px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '16px' : '14px'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        name="isVerified"
                        label="Account Verified"
                        valuePropName="checked"
                      >
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              },
              {
                key: '2',
                label: 'Profile',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="address" label="Address">
                        <Input.TextArea
                          placeholder="Enter address"
                          disabled={dialogMode === 'view'}
                          rows={3}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="dateOfBirth" label="Date of Birth">
                        <Input
                          type="date"
                          disabled={dialogMode === 'view'}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="gender" label="Gender">
                        <Select placeholder="Select gender" disabled={dialogMode === 'view'}>
                          <Option value="Male">Male</Option>
                          <Option value="Female">Female</Option>
                          <Option value="Other">Other</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="occupation" label="Occupation">
                        <Input placeholder="Enter occupation" disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              },
              {
                key: '3',
                label: 'Documents',
                children: (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Divider orientation="left">PAN Details</Divider>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="panNumber" label="PAN Number">
                        <Input
                          placeholder="Enter PAN number"
                          disabled={dialogMode === 'view'}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="panVerified" label="PAN Verified" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Divider orientation="left">Aadhar Details</Divider>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="aadharNumber" label="Aadhar Number">
                        <Input
                          placeholder="Enter Aadhar number"
                          disabled={dialogMode === 'view'}
                          maxLength={12}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="aadharVerified" label="Aadhar Verified" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Divider orientation="left">Bank Account Details</Divider>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="accountNumber" label="Account Number">
                        <Input placeholder="Enter account number" disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="ifscCode" label="IFSC Code">
                        <Input
                          placeholder="Enter IFSC code"
                          disabled={dialogMode === 'view'}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="bankName" label="Bank Name">
                        <Input placeholder="Enter bank name" disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="accountHolderName" label="Account Holder Name">
                        <Input placeholder="Enter account holder name" disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="bankVerified" label="Bank Verified" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              },
              {
                key: '4',
                label: 'Trading',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="experience" label="Trading Experience">
                        <Select placeholder="Select experience" disabled={dialogMode === 'view'}>
                          <Option value="beginner">Beginner</Option>
                          <Option value="intermediate">Intermediate</Option>
                          <Option value="advanced">Advanced</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="riskTolerance" label="Risk Tolerance">
                        <Select placeholder="Select risk tolerance" disabled={dialogMode === 'view'}>
                          <Option value="low">Low</Option>
                          <Option value="medium">Medium</Option>
                          <Option value="high">High</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="kycStatus" label="KYC Status">
                        <Select placeholder="Select KYC status" disabled={dialogMode === 'view'}>
                          <Option value="pending">Pending</Option>
                          <Option value="in_progress">In Progress</Option>
                          <Option value="completed">Completed</Option>
                          <Option value="rejected">Rejected</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                )
              },
              {
                key: '5',
                label: 'Preferences',
                children: (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Divider orientation="left">Notifications</Divider>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="emailNotifications" label="Email" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="smsNotifications" label="SMS" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="pushNotifications" label="Push" valuePropName="checked">
                        <Switch disabled={dialogMode === 'view'} />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Divider orientation="left">App Settings</Divider>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="theme" label="Theme">
                        <Select placeholder="Select theme" disabled={dialogMode === 'view'}>
                          <Option value="light">Light</Option>
                          <Option value="dark">Dark</Option>
                          <Option value="auto">Auto</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="language" label="Language">
                        <Select placeholder="Select language" disabled={dialogMode === 'view'}>
                          <Option value="en">English</Option>
                          <Option value="hi">Hindi</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                )
              }
            ]}
          />

          {dialogMode !== 'view' && (
            <div style={{ 
              marginTop: 24,
              padding: isMobile ? '16px 0' : '20px 0',
              borderTop: '1px solid #f0f0f0'
            }}>
              {isMobile ? (
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    block
                    style={{
                      height: '52px',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                    }}
                  >
                    {dialogMode === 'create' ? '✨ Create User' : '💾 Update User'}
                  </Button>
                  <Button 
                    onClick={handleCloseDialog}
                    block
                    style={{
                      height: '48px',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: '#f8fafc',
                      border: '2px solid #e2e8f0',
                      color: '#64748b'
                    }}
                  >
                    ❌ Cancel
                  </Button>
                </Space>
              ) : (
                <div style={{ textAlign: 'right' }}>
                  <Space size={12}>
                    <Button 
                      onClick={handleCloseDialog}
                      style={{
                        height: '44px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        padding: '0 24px'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="primary" 
                      htmlType="submit"
                      style={{
                        height: '44px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        padding: '0 24px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        border: 'none'
                      }}
                    >
                      {dialogMode === 'create' ? 'Create User' : 'Update User'}
                    </Button>
                  </Space>
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal>

      {/* Financial Management Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <WalletOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#722ed1' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                💰 Fund Management
              </div>
              <div style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                opacity: 0.8,
                marginTop: 2
              }}>
                {selectedUserFinancial?.name}
              </div>
            </div>
          </div>
        }
        open={financialModalOpen}
        onCancel={() => setFinancialModalOpen(false)}
        footer={null}
        width={isMobile ? '100%' : 1000}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '80vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        {financialLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : financialData ? (
          <div>
            {/* Enhanced Financial Summary */}
            <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    💰
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Available Balance</span>}
                    value={financialData.availableBalance}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    📊
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Portfolio Value</span>}
                    value={financialData.currentPortfolioValue}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: financialData.totalPnL >= 0 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    {financialData.totalPnL >= 0 ? '📈' : '📉'}
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Total P&L</span>}
                    value={Math.abs(financialData.totalPnL)}
                    precision={2}
                    prefix={financialData.totalPnL >= 0 ? '+₹' : '-₹'}
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    💎
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Total Capital</span>}
                    value={financialData.totalCapital}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Enhanced Fund Management Form */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    borderRadius: '8px',
                    padding: '6px',
                    color: 'white'
                  }}>
                    💰
                  </div>
                  <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>
                    Fund Management
                  </span>
                </div>
              }
              style={{ 
                marginBottom: isMobile ? 16 : 24,
                borderRadius: isMobile ? '16px' : '12px',
                border: '1px solid #f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}
              bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
            >
              <Form
                form={fundsForm}
                layout="vertical"
                onFinish={handleFundsOperation}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <Form.Item
                      name="action"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔄 Action</span>}
                      rules={[{ required: true, message: 'Select action' }]}
                    >
                      <Select 
                        placeholder="Select Action"
                        style={{
                          height: isMobile ? '48px' : '40px'
                        }}
                        size={isMobile ? 'large' : 'middle'}
                      >
                        <Option value="add">➕ Add Funds</Option>
                        <Option value="subtract">➖ Remove Funds</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <Form.Item
                      name="amount"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💵 Amount</span>}
                      rules={[
                        { required: true, message: 'Enter amount' },
                        {
                          validator: (_, value) => {
                            if (!value || value <= 0) {
                              return Promise.reject(new Error('Amount must be positive'));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        prefix="₹"
                        min={0.01}
                        step={0.01}
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <Form.Item 
                      name="reason"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📝 Reason (Optional)</span>}
                    >
                      <Input 
                        placeholder="Enter reason"
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <Form.Item 
                      name="reference"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔗 Reference (Optional)</span>}
                    >
                      <Input 
                        placeholder="Enter reference"
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item style={{ marginBottom: 0, marginTop: isMobile ? 8 : 16 }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        block={isMobile}
                        style={{
                          height: isMobile ? '52px' : '44px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px',
                          fontWeight: '700',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          border: 'none',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                          width: isMobile ? '100%' : 'auto',
                          minWidth: isMobile ? 'auto' : '120px'
                        }}
                      >
                        ⚡ Execute
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            {/* Trading Summary */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Trading Summary" size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Total Orders:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.totalOrders}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Executed Orders:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.executedOrders}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Pending Orders:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.pendingOrders}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Trading P&L:</span>
                    <span style={{
                      fontWeight: 'bold',
                      color: financialData.tradingPnL >= 0 ? '#52c41a' : '#ff4d4f'
                    }}>
                      ₹{financialData.tradingPnL?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Portfolio Summary" size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Total Holdings:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.totalHoldings}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Portfolio Allocation:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.portfolioAllocation?.toFixed(1) || '0.0'}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Cash Allocation:</span>
                    <span style={{ fontWeight: 'bold' }}>{financialData.cashAllocation?.toFixed(1) || '0.0'}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Portfolio P&L:</span>
                    <span style={{
                      fontWeight: 'bold',
                      color: financialData.portfolioPnL >= 0 ? '#52c41a' : '#ff4d4f'
                    }}>
                      ₹{financialData.portfolioPnL?.toFixed(2) || '0.00'} ({financialData.portfolioPnLPercent?.toFixed(2) || '0.00'}%)
                    </span>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        ) : null}
      </Modal>

      {/* Portfolio Management Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <PieChartOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#fa8c16' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                📊 Portfolio Management
              </div>
              <div style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                opacity: 0.8,
                marginTop: 2
              }}>
                {selectedUserFinancial?.name}
              </div>
            </div>
          </div>
        }
        open={portfolioModalOpen}
        onCancel={() => setPortfolioModalOpen(false)}
        footer={null}
        width={isMobile ? '100%' : 1200}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '80vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        {financialLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : portfolioData ? (
          <div>
            {/* Enhanced Portfolio Summary */}
            <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    💼
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Total Investment</span>}
                    value={portfolioData.summary.totalInvestment}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    💎
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Current Value</span>}
                    value={portfolioData.summary.currentValue}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: portfolioData.summary.totalPnL >= 0 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    {portfolioData.summary.totalPnL >= 0 ? '📈' : '📉'}
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>Total P&L</span>}
                    value={Math.abs(portfolioData.summary.totalPnL)}
                    precision={2}
                    prefix={portfolioData.summary.totalPnL >= 0 ? '+₹' : '-₹'}
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Card 
                  style={{
                    background: portfolioData.summary.totalPnLPercent >= 0 
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: 'none',
                    borderRadius: isMobile ? '12px' : '16px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? '80px' : '100px'
                  }}
                  bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    fontSize: isMobile ? '30px' : '40px',
                    opacity: 0.2,
                    color: 'white'
                  }}>
                    📊
                  </div>
                  <Statistic
                    title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '11px' : '12px', fontWeight: 500 }}>P&L %</span>}
                    value={Math.abs(portfolioData.summary.totalPnLPercent)}
                    precision={2}
                    suffix="%"
                    prefix={portfolioData.summary.totalPnLPercent >= 0 ? '+' : '-'}
                    valueStyle={{ color: 'white', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Enhanced Portfolio Management Form */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '8px',
                    padding: '6px',
                    color: 'white'
                  }}>
                    📊
                  </div>
                  <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>
                    Manage Holdings
                  </span>
                </div>
              }
              style={{ 
                marginBottom: isMobile ? 16 : 24,
                borderRadius: isMobile ? '12px' : '16px',
                border: '1px solid #f1f5f9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}
              bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
            >
              <Form
                layout="vertical"
                onFinish={handlePortfolioUpdate}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item
                      name="symbol"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📈 Symbol</span>}
                      rules={[{ required: true, message: 'Enter symbol' }]}
                    >
                      <Input 
                        placeholder="e.g., RELIANCE" 
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item
                      name="action"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>⚡ Action</span>}
                      rules={[{ required: true, message: 'Select action' }]}
                    >
                      <Select 
                        placeholder="Select action"
                        style={{
                          height: isMobile ? '48px' : '40px'
                        }}
                        size={isMobile ? 'large' : 'middle'}
                      >
                        <Option value="add">➕ Add</Option>
                        <Option value="update">🔄 Update</Option>
                        <Option value="remove">❌ Remove</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item 
                      name="quantity"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📦 Quantity</span>}
                    >
                      <Input
                        type="number"
                        placeholder="e.g., 100"
                        min={0}
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item 
                      name="avgPrice"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💰 Avg Price</span>}
                    >
                      <Input
                        type="number"
                        placeholder="e.g., 1250.50"
                        min={0}
                        step={0.01}
                        prefix="₹"
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8} lg={8}>
                    <Form.Item 
                      name="currentPrice"
                      label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💎 Current Price</span>}
                    >
                      <Input
                        type="number"
                        placeholder="e.g., 1390.75"
                        min={0}
                        step={0.01}
                        prefix="₹"
                        style={{
                          height: isMobile ? '48px' : '40px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '16px' : '14px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={16} lg={16}>
                    <div style={{ 
                      display: 'flex', 
                      gap: 12, 
                      alignItems: 'end',
                      height: '100%',
                      paddingTop: isMobile ? '32px' : '0'
                    }}>
                      {isMobile ? (
                        <Space direction="vertical" style={{ width: '100%' }} size={8}>
                          <Button 
                            type="primary" 
                            htmlType="submit"
                            block
                            style={{
                              height: '48px',
                              borderRadius: '12px',
                              fontSize: '16px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              border: 'none',
                              boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)'
                            }}
                          >
                            🔄 Update Portfolio
                          </Button>
                          <Popconfirm
                            title="Are you sure you want to clear the entire portfolio?"
                            onConfirm={handleClearPortfolio}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button 
                              danger
                              block
                              style={{
                                height: '44px',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: '600'
                              }}
                            >
                              🗑️ Clear All
                            </Button>
                          </Popconfirm>
                        </Space>
                      ) : (
                        <Space size={12}>
                          <Button 
                            type="primary" 
                            htmlType="submit"
                            style={{
                              height: '40px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              border: 'none'
                            }}
                          >
                            Update Portfolio
                          </Button>
                          <Popconfirm
                            title="Are you sure you want to clear the entire portfolio?"
                            onConfirm={handleClearPortfolio}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button 
                              danger
                              style={{
                                height: '40px',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              Clear All
                            </Button>
                          </Popconfirm>
                        </Space>
                      )}
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card>

            {/* Portfolio Holdings Table */}
            <Card title="Current Holdings">
              <Table
                dataSource={portfolioData.portfolio}
                rowKey="symbol"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: 'Symbol',
                    dataIndex: 'symbol',
                    key: 'symbol',
                    render: (symbol) => (
                      <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        {symbol}
                      </span>
                    )
                  },
                  {
                    title: 'Quantity',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    align: 'right'
                  },
                  {
                    title: 'Avg Price',
                    dataIndex: 'avgPrice',
                    key: 'avgPrice',
                    align: 'right',
                    render: (price) => `₹${price?.toFixed(2) || '0.00'}`
                  },
                  {
                    title: 'Current Price',
                    dataIndex: 'currentPrice',
                    key: 'currentPrice',
                    align: 'right',
                    render: (price) => `₹${price?.toFixed(2) || '0.00'}`
                  },
                  {
                    title: 'Investment',
                    dataIndex: 'investment',
                    key: 'investment',
                    align: 'right',
                    render: (investment) => `₹${investment?.toFixed(2) || '0.00'}`
                  },
                  {
                    title: 'Current Value',
                    dataIndex: 'currentValue',
                    key: 'currentValue',
                    align: 'right',
                    render: (value) => `₹${value?.toFixed(2) || '0.00'}`
                  },
                  {
                    title: 'P&L',
                    dataIndex: 'pnl',
                    key: 'pnl',
                    align: 'right',
                    render: (pnl) => (
                      <span style={{ color: pnl >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                        ₹{pnl?.toFixed(2) || '0.00'}
                      </span>
                    )
                  },
                  {
                    title: 'P&L %',
                    dataIndex: 'pnlPercent',
                    key: 'pnlPercent',
                    align: 'right',
                    render: (pnlPercent) => (
                      <span style={{ color: pnlPercent >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                        {pnlPercent?.toFixed(2) || '0.00'}%
                      </span>
                    )
                  }
                ]}
              />
            </Card>
          </div>
        ) : null}
      </Modal>

      {/* Transaction History Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <BankOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#13c2c2' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                🏦 Transaction History
              </div>
              <div style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                opacity: 0.8,
                marginTop: 2
              }}>
                {selectedUserFinancial?.name}
              </div>
            </div>
          </div>
        }
        open={transactionModalOpen}
        onCancel={() => setTransactionModalOpen(false)}
        footer={null}
        width={isMobile ? '100%' : 1000}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #13c2c2 0%, #08979c 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '80vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        {financialLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : transactionData ? (
          <div>
            {isMobile ? (
              /* Mobile Transaction List */
              <div>
                <div style={{
                  marginBottom: 16,
                  padding: 12,
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                  borderRadius: '12px',
                  border: '1px solid #0ea5e9',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>
                    📊 Total Transactions: {transactionData.transactions?.length || 0}
                  </span>
                </div>
                <List
                  dataSource={transactionData.transactions}
                  renderItem={(transaction, index) => (
                    <List.Item
                      key={`${transaction.timestamp}-${index}`}
                      style={{
                        padding: '16px',
                        marginBottom: '12px',
                        background: 'white',
                        borderRadius: '16px',
                        border: '1px solid #f1f5f9',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: 8
                        }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                              {formatDateIST(transaction.timestamp)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              {formatTimeIST(transaction.timestamp)}
                            </div>
                          </div>
                          <Tag 
                            color={transaction.type === 'credit' ? 'green' : 'red'}
                            style={{ 
                              borderRadius: '20px',
                              fontWeight: '600',
                              fontSize: '12px'
                            }}
                          >
                            {transaction.type === 'credit' ? '💰 CREDIT' : '💸 DEBIT'}
                          </Tag>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: 8
                        }}>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            📝 {transaction.description || 'Transaction'}
                          </span>
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: '700',
                            color: transaction.type === 'credit' ? '#10b981' : '#ef4444'
                          }}>
                            {transaction.type === 'credit' ? '+' : '-'}₹{Math.abs(transaction.amount)?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          paddingTop: 8,
                          borderTop: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            💳 Balance: ₹{transaction.balance?.toFixed(2) || '0.00'}
                          </span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            ID: {transaction._id?.slice(-6) || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </List.Item>
                  )}
                  pagination={{
                    pageSize: 8,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total, range) => 
                      `${range[0]}-${range[1]} of ${total} transactions`,
                    style: { textAlign: 'center', marginTop: 16 }
                  }}
                />
              </div>
            ) : (
              /* Desktop Transaction Table */
              <Table
                dataSource={transactionData.transactions}
                rowKey={(record, index) => `${record.timestamp}-${index}`}
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true
                }}
                scroll={{ x: 800 }}
                columns={[
                {
                  title: 'Date',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (timestamp) => (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>
                        {formatDateIST(timestamp)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {formatTimeIST(timestamp)}
                      </div>
                    </div>
                  ),
                  width: 100
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  render: (type) => (
                    <Tag color={type === 'credit' ? 'green' : 'red'}>
                      {type.toUpperCase()}
                    </Tag>
                  ),
                  width: 80
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: true
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right',
                  render: (amount, record) => (
                    <span style={{
                      color: record.type === 'credit' ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold'
                    }}>
                      {record.type === 'credit' ? '+' : '-'}₹{amount?.toFixed(2) || '0.00'}
                    </span>
                  ),
                  width: 120
                },
                {
                  title: 'Balance',
                  dataIndex: 'balance',
                  key: 'balance',
                  align: 'right',
                  render: (balance) => `₹${balance?.toFixed(2) || '0.00'}`,
                  width: 120
                },
                {
                  title: 'Reference',
                  dataIndex: 'reference',
                  key: 'reference',
                  render: (reference) => (
                    <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                      {reference || 'N/A'}
                    </span>
                  ),
                  width: 120
                }
              ]}
              />
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Users;